import React, { useRef, useCallback, useEffect } from 'react';
import './ResizeHandle.css';

const ResizeHandle = ({ onResize, minWidth = 250, maxWidth = 800, isPanelsSwitched = false }) => {
  const handleRef = useRef(null);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    
    // Use requestAnimationFrame for smooth 60fps updates
    requestAnimationFrame(() => {
      if (!isResizingRef.current) return;
      
      // When panels are switched, chat is on left, so dragging right increases width
      // When panels are normal, chat is on right, so dragging left increases width
      const diff = isPanelsSwitched 
        ? e.clientX - startXRef.current  // Normal: dragging right increases width
        : startXRef.current - e.clientX; // Inverted: dragging left increases width
      
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidthRef.current + diff)
      );
      
      onResize(newWidth);
    });
  }, [minWidth, maxWidth, onResize, isPanelsSwitched]);
  
  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.classList.remove('resizing');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    isResizingRef.current = true;
    startXRef.current = e.clientX;
    // Support both right-panel and conceptgate-panel
    const panel = e.target.closest('.right-panel') || e.target.closest('.conceptgate-panel');
    if (panel) {
      startWidthRef.current = parseInt(
        window.getComputedStyle(panel).width,
        10
      );
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.classList.add('resizing');
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [handleMouseMove, handleMouseUp]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isResizingRef.current) {
        handleMouseUp();
      }
    };
  }, [handleMouseUp]);
  
  return (
    <div
      ref={handleRef}
      className="right-panel-resize-handle"
      onMouseDown={handleMouseDown}
      aria-label="Resize chat panel"
      role="separator"
      aria-orientation="vertical"
    />
  );
};

export default ResizeHandle;

